import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { adminSignupGateway } from '../../services/AdminSignupGateway';
import { formatUsPhoneDisplay, usPhoneDigitsFromInput } from '../../../../shared/formatting';
import { friendlyErrorMessage } from '../../../../shared/friendlyError';
import type { AssignableRoleResponse, InvitationResponse } from '../../types';
import type { StepProps } from '../AdminSignUpPage';

/** A not-yet-invited additional admin the user is currently filling in — client-side only until "Save
 * Admin" succeeds, at which point it is replaced by the real `InvitationResponse` IRegistrationService
 * .CreateInvitationAsync returns. Nothing about this shape is ever sent anywhere until that explicit
 * action. */
interface AdminDraft {
  id: string;
  name: string;
  email: string;
  roleId: string;
  isOwner: boolean;
  officeNumber: string;
  phone: string;
  error: string | null;
  sending: boolean;
}

type AdminTabEntry = { kind: 'owner' } | { kind: 'invitation'; invitation: InvitationResponse } | { kind: 'draft'; draft: AdminDraft };

function tabKey(entry: AdminTabEntry): string {
  if (entry.kind === 'owner') return 'owner';
  if (entry.kind === 'invitation') return `invite:${entry.invitation.id}`;
  return `draft:${entry.draft.id}`;
}

/** The tab's stable, primary identity — "Owner" or "Admin N" — never replaced wholesale by an email, so
 * the tab strip stays compact and predictable even once data is entered (see {@link tabSecondaryLabel}). */
function tabPrimaryLabel(entry: AdminTabEntry, index: number, memberNoun: string): string {
  return entry.kind === 'owner' ? 'Owner' : `${memberNoun} ${index + 1}`;
}

/** An optional short secondary identifier shown below the primary label once useful data exists — the
 * person's NAME, never their email (email remains visible inside the active tab's own form/details). The
 * Owner's secondary label updates live while typing, before "Save Owner" ever runs — `ownerDisplayName` is
 * passed in explicitly since it's the one tab whose name lives in top-level component state rather than on
 * the tab entry itself. */
function tabSecondaryLabel(entry: AdminTabEntry, ownerDisplayName: string): string {
  if (entry.kind === 'owner') return ownerDisplayName.trim();
  if (entry.kind === 'invitation') return (entry.invitation.name ?? '').trim();
  return entry.draft.name.trim();
}

function tabHasError(entry: AdminTabEntry, ownerError: string | null): boolean {
  if (entry.kind === 'owner') return ownerError !== null;
  if (entry.kind === 'draft') return entry.draft.error !== null;
  return false;
}

let draftIdCounter = 0;
function nextDraftId(): string {
  draftIdCounter += 1;
  return `admin-draft-${draftIdCounter}`;
}

/**
 * Step 4 — Admin Information. A single primary signup step presented as a tabbed interface (Admin Sign Up
 * Step 4 UX correction pass): the approved Figma direction shows one admin per tab, never every admin
 * stacked vertically at once. Only the active tab's panel is rendered.
 * <p>
 * The registering Owner's tab (fixed, first, not removable) sets its own password here — matching Admin
 * Sign Up Decision 1/4: the registering user is always the Owner by construction (see
 * RegistrationService.CreateOwnerAsync/FinalizeAsync), so "at least one Owner" can never fail.
 * <p>
 * An additional-admin tab never collects a password — the static handoff's password field is intentionally
 * NOT reproduced there; this preserves the invitation architecture (Decision 4): an invited admin sets
 * their own credential when they accept, the registering Owner never chooses it for them. Admin Name,
 * Office Number, and Phone ARE collected, matching the approved Admin Information fields — Office
 * Number/Phone were already carried by `MembershipInvitation.IntendedOfficeNumber`/`IntendedPhone`
 * (applied to the invitee's own profile at acceptance); Admin Name is carried the identical way via the
 * new `IntendedDisplayName`, initializing the invitee's `User.DisplayName` only if they don't supply their
 * own at acceptance.
 * <p>
 * A draft (not yet invited) admin is client-side-only state; clicking "Save Admin" is the same
 * `createInvitation` call this screen already made, just scoped to one tab instead of one shared form. It
 * is deliberately not labeled "Send Invitation" — no real email/SMS delivery provider exists yet (see
 * `IInvitationNotifier`'s own doc comment), so an action verb implying a transmission just occurred would
 * be misleading. Removing a draft simply discards it; removing an already-sent invitation calls the
 * existing `revokeInvitation` endpoint — persistence timing is unchanged from before this pass.
 */
export interface AdminInfoStepProps extends StepProps {
  /** EPN-35 Partner Sign Up overrides — defaults match Admin Sign Up's own approved copy exactly. */
  heading?: string;
  /** Singular noun for an additional (non-Owner) team member — "Admin" for Admin Sign Up, "Team Member" for
   * Partner Sign Up. Used for the tab label ("Admin 1" / "Team Member 1"), the Add/Save button text, and
   * the draft panel's name field label. */
  memberNoun?: string;
  /** Plural, lowercase — used only in the "save the Owner first" guidance copy. */
  memberNounPluralLower?: string;
  /** The tab strip's accessible name — "Admins" for Admin Sign Up, "Team Members" for Partner Sign Up. */
  tabListAriaLabel?: string;
  /** Partner Sign Up only — the email/password already collected by AccountSetupStep, prefilled here so
   * the existing `createOwner` call this step already makes stays the single place that account is
   * actually created; see PartnerSignUpPage's own doc comment. Admin Sign Up never passes these (both
   * default to empty, identical to today's behavior). */
  initialOwnerEmail?: string;
  initialOwnerPassword?: string;
}

export function AdminInfoStep({
  goNext,
  goBack,
  heading = 'Step 4: Admin Information',
  memberNoun = 'Admin',
  memberNounPluralLower = 'administrators',
  tabListAriaLabel = 'Admins',
  initialOwnerEmail = '',
  initialOwnerPassword = '',
}: AdminInfoStepProps) {
  const [ownerEmail, setOwnerEmail] = useState(initialOwnerEmail);
  const [ownerPassword, setOwnerPassword] = useState(initialOwnerPassword);
  const [ownerPasswordVisible, setOwnerPasswordVisible] = useState(false);
  const [ownerDisplayName, setOwnerDisplayName] = useState('');
  const [ownerOfficeNumber, setOwnerOfficeNumber] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [ownerCreated, setOwnerCreated] = useState(false);
  const [ownerError, setOwnerError] = useState<string | null>(null);

  const [roles, setRoles] = useState<AssignableRoleResponse[]>([]);
  const [invitations, setInvitations] = useState<InvitationResponse[]>([]);
  const [drafts, setDrafts] = useState<AdminDraft[]>([]);
  const [activeTab, setActiveTab] = useState('owner');

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const draftNameRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [pendingFocus, setPendingFocus] = useState<{ key: string; target: 'tab' | 'name' } | null>(null);

  useEffect(() => {
    void adminSignupGateway.listAssignableRoles().then(setRoles);
    void adminSignupGateway.listInvitations().then(setInvitations);
  }, []);

  // Runs after the render that added/activated a tab has committed, so the target element (the new
  // draft's Name input, or the tab button itself) is guaranteed to already exist in the DOM — more
  // deterministic than a requestAnimationFrame callback racing the next render. Each call site passes a
  // brand-new object, so this only re-runs on an actual new focus request — there is nothing to reset
  // back to null afterward.
  useEffect(() => {
    if (!pendingFocus) return;
    const el = pendingFocus.target === 'tab' ? tabRefs.current[pendingFocus.key] : draftNameRefs.current[pendingFocus.key];
    el?.focus();
  }, [pendingFocus]);

  const tabEntries: AdminTabEntry[] = [
    { kind: 'owner' },
    ...invitations.map((invitation): AdminTabEntry => ({ kind: 'invitation', invitation })),
    ...drafts.map((draft): AdminTabEntry => ({ kind: 'draft', draft })),
  ];

  function activateTab(key: string, focusTab = false) {
    setActiveTab(key);
    if (focusTab) {
      setPendingFocus({ key, target: 'tab' });
    }
  }

  function handleTabListKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const count = tabEntries.length;
    let nextIndex = index;
    if (e.key === 'ArrowRight') nextIndex = (index + 1) % count;
    else if (e.key === 'ArrowLeft') nextIndex = (index - 1 + count) % count;
    else if (e.key === 'Home') nextIndex = 0;
    else if (e.key === 'End') nextIndex = count - 1;

    // tabEntries always contains the Owner entry, and nextIndex is bounded to the valid tab range above.
    const nextKey = tabKey(tabEntries[nextIndex]!);
    setActiveTab(nextKey);
    tabRefs.current[nextKey]?.focus();
  }

  function handleAddAdminTab() {
    const draft: AdminDraft = {
      id: nextDraftId(),
      name: '',
      email: '',
      roleId: roles[0]?.id ?? '',
      isOwner: false,
      officeNumber: '',
      phone: '',
      error: null,
      sending: false,
    };
    setDrafts((prev) => [...prev, draft]);
    const key = `draft:${draft.id}`;
    setActiveTab(key);
    setPendingFocus({ key, target: 'name' });
  }

  function updateDraft(id: string, patch: Partial<AdminDraft>) {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  }

  function neighboringTabKeyAfterRemoving(removedKey: string): string {
    const index = tabEntries.findIndex((entry) => tabKey(entry) === removedKey);
    const remaining = tabEntries.filter((entry) => tabKey(entry) !== removedKey);
    if (remaining.length === 0) return 'owner';
    // The check above is the invariant establishing that remaining[0] exists.
    const neighbor = remaining[Math.min(index - 1, remaining.length - 1)] ?? remaining[0]!;
    return tabKey(neighbor);
  }

  function handleRemoveDraft(id: string) {
    const key = `draft:${id}`;
    const nextKey = neighboringTabKeyAfterRemoving(key);
    setDrafts((prev) => prev.filter((d) => d.id !== id));
    activateTab(nextKey, true);
  }

  async function handleRemoveInvitation(invitationId: string) {
    const key = `invite:${invitationId}`;
    const nextKey = neighboringTabKeyAfterRemoving(key);
    await adminSignupGateway.revokeInvitation(invitationId);
    setInvitations((prev) => prev.filter((i) => i.id !== invitationId));
    activateTab(nextKey, true);
  }

  async function handleCreateOwner() {
    setOwnerError(null);
    if (!ownerDisplayName || !ownerEmail || !ownerPassword || !ownerOfficeNumber || !ownerPhone) {
      setOwnerError('Complete all required admin fields before saving.');
      return;
    }
    try {
      await adminSignupGateway.createOwner({
        email: ownerEmail,
        password: ownerPassword,
        displayName: ownerDisplayName || null,
        officeNumber: ownerOfficeNumber || null,
        phone: ownerPhone || null,
      });
      setOwnerCreated(true);
    } catch (err) {
      setOwnerError(friendlyErrorMessage(err, 'Could not create the Owner account'));
    }
  }

  async function handleSaveAdmin(id: string) {
    const draft = drafts.find((d) => d.id === id);
    if (!draft) return;
    updateDraft(id, { error: null });
    if (!ownerCreated) {
      updateDraft(id, { error: 'Create your own Owner account before adding additional admins.' });
      return;
    }
    updateDraft(id, { sending: true });
    try {
      const created = await adminSignupGateway.createInvitation({
        email: draft.email,
        name: draft.name || null,
        roleId: draft.roleId,
        isOwner: draft.isOwner,
        officeNumber: draft.officeNumber || null,
        phone: draft.phone || null,
      });
      setInvitations((prev) => [...prev, created]);
      setDrafts((prev) => prev.filter((d) => d.id !== id));
      setActiveTab(`invite:${created.id}`);
    } catch (err) {
      updateDraft(id, { error: friendlyErrorMessage(err, 'Could not save this admin'), sending: false });
    }
  }

  function firstErrorTabKey(): string | null {
    if (!ownerCreated) return 'owner';
    const draftWithError = drafts.find((d) => d.error);
    return draftWithError ? `draft:${draftWithError.id}` : null;
  }

  async function handleSubmit() {
    setSubmitError(null);
    const errorKey = firstErrorTabKey();
    if (errorKey !== null) {
      // The specific error is already shown inside the tab we're switching to (ownerError, or the
      // draft's own error) — no separate banner is needed and would only duplicate the same alert.
      if (errorKey === 'owner') {
        setOwnerError((current) => current ?? 'Select at least one admin account as the owner to continue.');
      }
      activateTab(errorKey, true);
      return;
    }
    setSubmitting(true);
    try {
      await adminSignupGateway.finalize();
      goNext();
    } catch (err) {
      setSubmitError(friendlyErrorMessage(err, 'Could not complete sign up'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="screen active" data-testid="admin-info-step">
      <div className="screen-icon" aria-hidden="true">
        👤
      </div>
      <h2>{heading}</h2>

      <div className="admin-tabs-row">
        <div className="admin-tabs" role="tablist" aria-label={tabListAriaLabel}>
          {tabEntries.map((entry, index) => {
            const key = tabKey(entry);
            const primaryLabel = tabPrimaryLabel(entry, index, memberNoun);
            const secondaryLabel = tabSecondaryLabel(entry, ownerDisplayName);
            const hasError = tabHasError(entry, ownerError);
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                ref={(el) => {
                  tabRefs.current[key] = el;
                }}
                type="button"
                role="tab"
                id={`admin-tab-${key}`}
                aria-selected={isActive}
                aria-controls={`admin-tabpanel-${key}`}
                aria-invalid={hasError || undefined}
                tabIndex={isActive ? 0 : -1}
                title={secondaryLabel || undefined}
                className={`admin-tab${isActive ? ' admin-tab--active' : ''}${hasError ? ' admin-tab--error' : ''}`}
                onClick={() => setActiveTab(key)}
                onKeyDown={(e) => handleTabListKeyDown(e, index)}
              >
                {hasError && (
                  <span className="admin-tab-error-badge" aria-hidden="true" title="This tab needs attention">
                    !
                  </span>
                )}
                <span className="admin-tab-text">
                  <span className="admin-tab-label">{primaryLabel}</span>
                  {secondaryLabel && <span className="admin-tab-sub">{secondaryLabel}</span>}
                </span>
                {hasError && <span className="sr-only"> (needs attention)</span>}
              </button>
            );
          })}
        </div>
        <div className="add-admin-wrap">
          <button
            className="add-admin"
            type="button"
            onClick={handleAddAdminTab}
            disabled={!ownerCreated}
            aria-describedby={!ownerCreated ? 'add-admin-guidance' : undefined}
          >
            <span className="plus-circle" aria-hidden="true">
              +
            </span>
            Add {memberNoun}
          </button>
          {!ownerCreated && (
            <span className="add-admin-guidance" id="add-admin-guidance">
              Save the Owner account before adding additional {memberNounPluralLower}.
            </span>
          )}
        </div>
      </div>

      {tabEntries.map((entry) => {
        const key = tabKey(entry);
        if (key !== activeTab) return null;

        if (entry.kind === 'owner') {
          return (
            <div key={key} role="tabpanel" id={`admin-tabpanel-${key}`} aria-labelledby={`admin-tab-${key}`} tabIndex={0}>
              <div className="field full">
                <label htmlFor="ownerName">
                  Admin Name <span className="req">*</span>
                </label>
                <input className="control" id="ownerName" value={ownerDisplayName} onChange={(e) => setOwnerDisplayName(e.target.value)} disabled={ownerCreated} required />
              </div>

              <div style={{ height: 12 }} />
              {/* The registering Owner IS the Owner by construction (see FinalizeAsync) — there is no
                  choice to make here, so this is a plain status indicator, never an interactive control
                  that would misleadingly imply one. */}
              <span className="owner-status-badge" data-testid="owner-status-badge">
                Owner
              </span>

              <div className="field-grid" style={{ marginTop: 17 }}>
                <div className="field">
                  <label htmlFor="ownerEmail">
                    Email <span className="req">*</span>
                  </label>
                  <input
                    className="control"
                    id="ownerEmail"
                    type="email"
                    value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                    disabled={ownerCreated}
                    placeholder="name@company.com"
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="field">
                  <label htmlFor="ownerPassword">
                    Password <span className="req">*</span>
                  </label>
                  <div className="password-wrap">
                    <input
                      className="control"
                      id="ownerPassword"
                      type={ownerPasswordVisible ? 'text' : 'password'}
                      value={ownerPassword}
                      onChange={(e) => setOwnerPassword(e.target.value)}
                      disabled={ownerCreated}
                      autoComplete="new-password"
                      minLength={8}
                      required
                    />
                    <button
                      className="eye-btn"
                      type="button"
                      aria-label={ownerPasswordVisible ? 'Hide password' : 'Show password'}
                      onClick={() => setOwnerPasswordVisible((v) => !v)}
                      disabled={ownerCreated}
                    >
                      {ownerPasswordVisible ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>
              </div>

              <p className="info-line">
                <span className="info-icon">i</span>
                <span>Use your email and password to sign in to your account.</span>
              </p>

              <div className="field-grid" style={{ marginTop: 17 }}>
                <div className="field">
                  <label htmlFor="ownerOfficeNumber">
                    Office Number <span className="req">*</span>
                  </label>
                  <input
                    className="control"
                    id="ownerOfficeNumber"
                    type="tel"
                    inputMode="tel"
                    value={formatUsPhoneDisplay(ownerOfficeNumber)}
                    onChange={(e) => setOwnerOfficeNumber(usPhoneDigitsFromInput(e.target.value))}
                    disabled={ownerCreated}
                    placeholder="(XXX) XXX-XXXX"
                    autoComplete="tel"
                    maxLength={14}
                    required
                  />
                </div>

                <div className="field">
                  <label htmlFor="ownerPhone">
                    Phone <span className="req">*</span>
                  </label>
                  <input
                    className="control"
                    id="ownerPhone"
                    type="tel"
                    inputMode="tel"
                    value={formatUsPhoneDisplay(ownerPhone)}
                    onChange={(e) => setOwnerPhone(usPhoneDigitsFromInput(e.target.value))}
                    disabled={ownerCreated}
                    placeholder="(XXX) XXX-XXXX"
                    autoComplete="tel"
                    maxLength={14}
                    required
                  />
                </div>
              </div>

              {ownerError && (
                <p className="error-line" role="alert" id="ownerError">
                  <span className="info-icon">i</span>
                  <span>{ownerError}</span>
                </p>
              )}

              <div className="actions" style={{ justifyContent: 'flex-start', marginTop: 20 }}>
                {!ownerCreated && (
                  <button className="btn btn-primary" type="button" onClick={() => void handleCreateOwner()}>
                    Save Owner & Continue
                  </button>
                )}
                {ownerCreated && <p className="info-line" style={{ margin: 0 }}>✓ Owner account created.</p>}
              </div>
            </div>
          );
        }

        if (entry.kind === 'invitation') {
          const invitation = entry.invitation;
          const roleName = roles.find((r) => r.id === invitation.roleId)?.name ?? invitation.roleId;
          return (
            <div key={key} role="tabpanel" id={`admin-tabpanel-${key}`} aria-labelledby={`admin-tab-${key}`} tabIndex={0}>
              <div className="field full">
                <label>Admin Name</label>
                <p className="control-static">{invitation.name ?? '—'}</p>
              </div>

              <div style={{ height: 12 }} />
              <label className="checkbox-row">
                <input type="checkbox" checked={invitation.isOwner} readOnly disabled />
                <span>Set as Owner</span>
              </label>

              <div className="field-grid" style={{ marginTop: 17 }}>
                <div className="field">
                  <label>Email</label>
                  <p className="control-static">{invitation.inviteeEmail}</p>
                </div>
                <div className="field">
                  <label>Role</label>
                  <p className="control-static">{roleName}</p>
                </div>
              </div>

              <div className="field-grid" style={{ marginTop: 17 }}>
                <div className="field">
                  <label>Office Number</label>
                  <p className="control-static">{invitation.officeNumber ? formatUsPhoneDisplay(invitation.officeNumber) : '—'}</p>
                </div>
                <div className="field">
                  <label>Phone</label>
                  <p className="control-static">{invitation.phone ? formatUsPhoneDisplay(invitation.phone) : '—'}</p>
                </div>
              </div>

              <p className="info-line">
                <span className="info-icon">i</span>
                <span>
                  This administrator will be invited to create their own password. Status: <strong>{invitation.status}</strong>.
                </span>
              </p>

              <div className="actions" style={{ justifyContent: 'flex-start' }}>
                <button className="btn btn-back" type="button" onClick={() => void handleRemoveInvitation(invitation.id)}>
                  Remove
                </button>
              </div>
            </div>
          );
        }

        const draft = entry.draft;
        return (
          <div key={key} role="tabpanel" id={`admin-tabpanel-${key}`} aria-labelledby={`admin-tab-${key}`} tabIndex={0}>
            <div className="field full">
              <label htmlFor={`${key}-name`}>{memberNoun} Name</label>
              <input
                className="control"
                id={`${key}-name`}
                ref={(el) => {
                  draftNameRefs.current[key] = el;
                }}
                value={draft.name}
                onChange={(e) => updateDraft(draft.id, { name: e.target.value })}
              />
            </div>

            <div className="field-grid" style={{ marginTop: 17 }}>
              <div className="field">
                <label htmlFor={`${key}-email`}>Email</label>
                <input
                  className="control"
                  id={`${key}-email`}
                  type="email"
                  value={draft.email}
                  onChange={(e) => updateDraft(draft.id, { email: e.target.value })}
                  placeholder="name@company.com"
                />
              </div>

              <div className="field">
                <label htmlFor={`${key}-role`}>Role</label>
                <select className="control" id={`${key}-role`} value={draft.roleId} onChange={(e) => updateDraft(draft.id, { roleId: e.target.value })}>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor={`${key}-office`}>Office Number</label>
                <input
                  className="control"
                  id={`${key}-office`}
                  type="tel"
                  inputMode="tel"
                  value={formatUsPhoneDisplay(draft.officeNumber)}
                  onChange={(e) => updateDraft(draft.id, { officeNumber: usPhoneDigitsFromInput(e.target.value) })}
                  placeholder="(XXX) XXX-XXXX"
                />
              </div>

              <div className="field">
                <label htmlFor={`${key}-phone`}>Phone</label>
                <input
                  className="control"
                  id={`${key}-phone`}
                  type="tel"
                  inputMode="tel"
                  value={formatUsPhoneDisplay(draft.phone)}
                  onChange={(e) => updateDraft(draft.id, { phone: usPhoneDigitsFromInput(e.target.value) })}
                  placeholder="(XXX) XXX-XXXX"
                />
              </div>
            </div>

            <label className="checkbox-row" htmlFor={`${key}-isOwner`} style={{ marginTop: 12 }}>
              <input id={`${key}-isOwner`} type="checkbox" checked={draft.isOwner} onChange={(e) => updateDraft(draft.id, { isOwner: e.target.checked })} />
              <span>Set as Owner</span>
            </label>

            <p className="info-line">
              <span className="info-icon">i</span>
              <span>This administrator will be invited to create their own password — the registering Owner never chooses it for them.</span>
            </p>

            {draft.error && (
              <p className="error-line" role="alert">
                <span className="info-icon">i</span>
                <span>{draft.error}</span>
              </p>
            )}

            <div className="actions" style={{ justifyContent: 'flex-start' }}>
              <button className="btn btn-back" type="button" onClick={() => handleRemoveDraft(draft.id)}>
                Remove
              </button>
              <button className="btn btn-primary" type="button" onClick={() => void handleSaveAdmin(draft.id)} disabled={draft.sending}>
                {draft.sending ? 'Saving…' : `Save ${memberNoun}`}
              </button>
            </div>
          </div>
        );
      })}

      {/* Admin Sign Up Decision 8: general Terms/Privacy acknowledgement lives at final Submit, with no
          separate checkbox — this is NOT authorization for ACH/payroll funding/tax payment/filing, which
          remain separate, out-of-scope-for-this-feature workflow stages. Kept outside the tab panels
          because it — like Go Back/Submit below — applies to the whole signup flow, not one admin. */}
      <p className="info-line">
        <span className="info-icon">i</span>
        <span>
          By clicking Complete Sign Up, you agree to the Escochex{' '}
          <a href="https://www.escochex.com/users-terms-and-conditions" target="_blank" rel="noreferrer">
            Terms of Service
          </a>{' '}
          and acknowledge the{' '}
          <a href="https://www.escochex.com/privacy-policy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          .
        </span>
      </p>

      {submitError && (
        <p className="error-line" role="alert">
          <span className="info-icon">i</span>
          <span>{submitError}</span>
        </p>
      )}

      {/* CTA hierarchy correction: the final completion action only ever appears once it's the ONLY
          sensible next step — the Owner is saved and no admin draft is sitting unsaved — so it never
          visually competes with "Save Owner & Continue" or a draft's own "Save Admin". While it isn't
          available yet, a plain status line explains why instead of showing a doomed/disabled duplicate
          of the same button (which would just invite an error a user has already been steered away from). */}
      {!(ownerCreated && drafts.length === 0) && (
        <p className="info-line" data-testid="complete-signup-guidance">
          <span className="info-icon">i</span>
          <span>
            {!ownerCreated
              ? 'Save the Owner account to continue.'
              : 'Save or remove the admin draft you are editing to continue.'}
          </span>
        </p>
      )}

      <div className="actions">
        <button className="btn btn-back" type="button" onClick={goBack}>
          Go Back
        </button>
        {ownerCreated && drafts.length === 0 && (
          <button className="btn btn-primary" type="button" onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Completing…' : 'Complete Sign Up'}
          </button>
        )}
      </div>
    </div>
  );
}
